import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { paiementService, documentService } from '../../api/services';
import {
  Document,
  ModePaiement,
  TypeDocument,
  MODE_PAIEMENT_LABELS,
  StatutPaiement,
} from '../../types';
import { formatTND } from '../../utils/formatters';
import { MoneyDisplay } from '../../components/common/MoneyDisplay';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import toast from 'react-hot-toast';

interface PaiementFormData {
  documentId: number;
  datePaiement: string;
  montant: number;
  modePaiement: ModePaiement;
  reference: string;
  banque: string;
  dateEcheance: string;
  notes: string;
}

export function PaiementForm() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedDocId = searchParams.get('documentId');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<PaiementFormData>({
    defaultValues: {
      datePaiement: new Date().toISOString().split('T')[0],
      modePaiement: ModePaiement.VIREMENT,
      montant: 0,
      reference: '',
      banque: '',
      dateEcheance: '',
      notes: '',
    },
  });

  const watchedDocId = watch('documentId');

  useEffect(() => {
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (watchedDocId && watchedDocId > 0) {
      const doc = documents.find((d) => d.id === Number(watchedDocId));
      if (doc) {
        setSelectedDocument(doc);
        setValue('montant', doc.resteAPayer);
      }
    }
  }, [watchedDocId, documents, setValue]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      // Factures de vente (encaissements) ET d'achat (règlements fournisseurs).
      const [ventes, achats] = await Promise.all([
        documentService.getAll({ typeDocument: TypeDocument.FACTURE, sens: 'VENTE', size: 500 }),
        documentService.getAll({ typeDocument: TypeDocument.FACTURE, sens: 'ACHAT', size: 500 }),
      ]);
      const unpaid = [...ventes.content, ...achats.content].filter(
        (d) =>
          d.statutPaiement !== StatutPaiement.PAYEE && d.resteAPayer > 0
      );
      setDocuments(unpaid);

      if (preselectedDocId) {
        const docId = parseInt(preselectedDocId);
        setValue('documentId', docId);
        const doc = unpaid.find((d) => d.id === docId);
        if (doc) {
          setSelectedDocument(doc);
          setValue('montant', doc.resteAPayer);
        }
      }
    } catch (error) {
      toast.error(t.messages.loadError);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: PaiementFormData) => {
    if (!data.documentId || data.documentId === 0) {
      toast.error('Veuillez sélectionner un document');
      return;
    }
    try {
      setSaving(true);
      await paiementService.create({
        documentId: Number(data.documentId),
        datePaiement: data.datePaiement,
        montant: data.montant,
        modePaiement: data.modePaiement,
        reference: data.reference || undefined,
        banque: data.banque || undefined,
        dateEcheance: data.dateEcheance || undefined,
        notes: data.notes || undefined,
      });
      toast.success(t.messages.paymentSuccess);
      navigate('/paiements');
    } catch (error) {
      toast.error(t.messages.saveError);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/paiements')} className="btn-outline p-2">
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <h1 className="page-title">{t.paiements.newPaiement}</h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {t.paiements.document}
          </h2>

          <div>
            <label className="label">{t.paiements.document} *</label>
            <select
              {...register('documentId', { required: t.validation.required, valueAsNumber: true })}
              className={`input-field ${errors.documentId ? 'input-error' : ''}`}
            >
              <option value={0}>Sélectionner une facture...</option>
              {documents.map((doc) => (
                <option key={doc.id} value={doc.id}>
                  {doc.sensDocument === 'ACHAT' ? '[Achat] ' : '[Vente] '}
                  {doc.numero} - {doc.clientRaisonSociale} - Reste: {formatTND(doc.resteAPayer)}
                </option>
              ))}
            </select>
          </div>

          {selectedDocument && (
            <div className="mt-4 bg-blue-50 rounded-lg p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-gray-500">{t.documents.numero}</p>
                  <p className="text-sm font-medium">{selectedDocument.numero}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">{t.documents.client}</p>
                  <p className="text-sm font-medium">{selectedDocument.clientRaisonSociale}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">{t.documents.netAPayer}</p>
                  <MoneyDisplay amount={selectedDocument.netAPayer} size="sm" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">{t.documents.resteAPayer}</p>
                  <MoneyDisplay amount={selectedDocument.resteAPayer} size="sm" className="text-orange-600 font-bold" />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Détails du paiement
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">{t.paiements.datePaiement} *</label>
              <input
                type="date"
                {...register('datePaiement', { required: t.validation.required })}
                className={`input-field ${errors.datePaiement ? 'input-error' : ''}`}
              />
            </div>

            <div>
              <label className="label">{t.paiements.montant} *</label>
              <div className="relative">
                <input
                  type="number"
                  step="0.001"
                  min="0.001"
                  {...register('montant', {
                    required: t.validation.required,
                    valueAsNumber: true,
                    min: { value: 0.001, message: t.validation.positiveNumber },
                  })}
                  className={`input-field pr-16 ${errors.montant ? 'input-error' : ''}`}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                  TND
                </span>
              </div>
              {errors.montant && (
                <p className="mt-1 text-sm text-red-600">{errors.montant.message}</p>
              )}
            </div>

            <div>
              <label className="label">{t.paiements.modePaiement} *</label>
              <select
                {...register('modePaiement', { required: t.validation.required })}
                className="input-field"
              >
                {Object.entries(MODE_PAIEMENT_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="label">{t.paiements.reference}</label>
              <input
                {...register('reference')}
                className="input-field"
                placeholder="N° chèque, référence virement..."
              />
            </div>

            <div>
              <label className="label">{t.paiements.banque}</label>
              <input
                {...register('banque')}
                className="input-field"
              />
            </div>

            <div>
              <label className="label">{t.paiements.dateEcheance}</label>
              <input
                type="date"
                {...register('dateEcheance')}
                className="input-field"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="label">{t.common.notes}</label>
            <textarea
              {...register('notes')}
              rows={2}
              className="input-field"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3">
          <button type="button" onClick={() => navigate('/paiements')} className="btn-outline">
            {t.common.cancel}
          </button>
          <button type="submit" disabled={saving} className="btn-primary">
            {saving ? t.common.loading : t.common.save}
          </button>
        </div>
      </form>
    </div>
  );
}

export default PaiementForm;
